import { useEffect, useMemo, useState } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { buildShippingsFromRouteMonitoring } from '@/pages/Business/pages/Shippings/pages/RouteBuilder/utils/build-shippings-from-route-monitoring';
import type { ShippingForRouting } from '@/pages/Business/pages/Shippings/models/routing.types';
import {
  resolveVisitDisplayLatLong,
  resolveVisitShippingEntries,
} from '@/pages/Business/pages/Monitoring/utils/resolve-visit-shipping-addresses';
import { VisitStatus } from '../enums';
import { RouteMonitoring } from '../models';
import type { RouteVisitShippingFilters } from '../hooks';
import VisitCard from './VisitCard';

interface VisitListProps {
  visits: RouteMonitoring['visits'];
  routeMonitoring: RouteMonitoring;
  shippingFilters?: RouteVisitShippingFilters;
  enrichedByNumber: Map<number, ShippingForRouting>;
  onVisitMapFocus: (visitLatitude: number, visitLongitude: number) => void;
  syncSearchFromRouteShippingNumber?: string;
}

const VisitList = ({
  visits,
  routeMonitoring,
  shippingFilters,
  enrichedByNumber,
  onVisitMapFocus,
  syncSearchFromRouteShippingNumber,
}: VisitListProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fromRoute = syncSearchFromRouteShippingNumber?.trim();
    if (fromRoute) {
      setSearchTerm(fromRoute);
    }
  }, [syncSearchFromRouteShippingNumber]);

  const filteredVisits = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return visits;

    return visits.filter((v) => {
      const sNum = String(v.shippingNumber || '').toLowerCase();
      const extId = String(v.externalId || '').toLowerCase();
      return sNum.includes(term) || extId.includes(term);
    });
  }, [visits, searchTerm]);

  const sortedVisits = useMemo(() => {
    return [...filteredVisits].sort(
      (firstVisit, secondVisit) => firstVisit.order - secondVisit.order
    );
  }, [filteredVisits]);

  const shippingByNumber = useMemo(() => {
    const shippings = buildShippingsFromRouteMonitoring(routeMonitoring);
    return new Map(shippings.map((s) => [s.number, s]));
  }, [routeMonitoring]);

  const baseVisitId = useMemo(() => {
    return [...visits]
      .sort((a, b) => a.order - b.order)
      .find(
        (routeVisit) =>
          !(routeVisit.shippings && routeVisit.shippings.length > 0)
      )?.id;
  }, [visits]);

  return (
    <div className='flex flex-col gap-4'>
      <TextField
        size='small'
        fullWidth
        placeholder='Buscar por Nº Pedido o ID Externo...'
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          backgroundColor: 'white',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': {
              borderColor: '#e5e7eb',
            },
            '&:hover fieldset': {
              borderColor: 'var(--primary-color)',
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon sx={{ color: 'gray', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
      />

      <div className='flex flex-col gap-3'>
        {sortedVisits.length > 0 ? (
          sortedVisits.map((visit) => {
            const isBase = baseVisitId !== undefined && visit.id === baseVisitId;
            const routeStopOrder: number | 'base' = isBase ? 'base' : visit.order;

            const shipping = shippingByNumber.get(visit.shippingNumber);
            const shippingEntries = resolveVisitShippingEntries(
              visit,
              enrichedByNumber
            )
              .filter(
                (entry) => shippingFilters?.matchesShipping(entry.number) ?? true
              )
              .map((entry) => ({
                ...entry,
                ...(shippingFilters?.getShippingLabels(entry.number) ?? {}),
              }));
            const { lat: visitLatitude, long: visitLongitude } =
              resolveVisitDisplayLatLong(visit, shippingEntries, shipping);
            const canFocus =
              Number.isFinite(visitLatitude) &&
              Number.isFinite(visitLongitude) &&
              !(visitLatitude === 0 && visitLongitude === 0);
            const handleMapFocus = canFocus
              ? () => onVisitMapFocus(visitLatitude, visitLongitude)
              : undefined;

            return (
              <VisitCard
                key={visit.id}
                routeStopOrder={routeStopOrder}
                visitedAt={visit.visitedAt}
                scheduledFrom={visit.scheduledFrom}
                status={visit.status as VisitStatus}
                address={visit.address}
                shippingNumber={visit.shippingNumber}
                shippings={visit.shippings}
                shippingEntries={shippingEntries}
                externalId={visit.externalId}
                packagesCount={visit.packagesCount}
                packagesPicked={visit.packagesPicked}
                onRequestMapFocus={handleMapFocus}
              />
            );
          })
        ) : (
          <div className='text-center text-gray-500 py-8'>
            {searchTerm
              ? 'No se encontraron resultados para la búsqueda'
              : shippingFilters?.isActive
                ? 'No hay pedidos con esos filtros'
                : 'No hay visitas registradas'}
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitList;
